import { FormEvent, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  api,
  type LivePolicy,
  type Permission,
  type Schema,
  type TableRLS,
} from "@/lib/api";
import {
  EmptyState,
  ErrorBox,
  PageHeader,
  Spinner,
  SuccessBox,
  WarnBox,
} from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import { buildPermissionBody } from "@/lib/permissionsBody";
import {
  buildPolicyBody,
  POLICY_COMMANDS,
  policyCommandAllowsCheck,
  policyCommandAllowsUsing,
  policyPresetExpression,
} from "@/lib/policyBody";

const LEVELS = [
  "disabled",
  "public",
  "authenticated",
  "owner",
  "admin",
  "custom",
];

export function PermissionsPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [schema, setSchema] = useState<Schema | null>(null);
  const [perms, setPerms] = useState<Record<string, Permission>>({});
  const [presets, setPresets] = useState<{ name: string }[]>([]);
  const [tableRLS, setTableRLS] = useState<TableRLS | null>(null);
  const [policyName, setPolicyName] = useState("");
  const [policyCommand, setPolicyCommand] = useState("SELECT");
  const [policyRoles, setPolicyRoles] = useState("");
  const [policyUsing, setPolicyUsing] = useState("user_id = auth.uid()");
  const [policyCheck, setPolicyCheck] = useState(
    "auth.role() = 'authenticated' AND user_id = auth.uid()",
  );
  const [editingPolicy, setEditingPolicy] = useState<string | null>(null);
  const [table, setTable] = useState("");
  const [preset, setPreset] = useState("personal_data");
  const [advanced, setAdvanced] = useState(false);
  const [select, setSelect] = useState("owner");
  const [insert, setInsert] = useState("authenticated");
  const [update, setUpdate] = useState("owner");
  const [del, setDel] = useState("owner");
  const [ownerColumn, setOwnerColumn] = useState("user_id");
  const [selectUsing, setSelectUsing] = useState("user_id = auth.uid()");
  const [insertCheck, setInsertCheck] = useState(
    "auth.role() = 'authenticated' AND user_id = auth.uid()",
  );
  const [updateUsing, setUpdateUsing] = useState("user_id = auth.uid()");
  const [updateCheck, setUpdateCheck] = useState("user_id = auth.uid()");
  const [deleteUsing, setDeleteUsing] = useState(
    "user_id = auth.uid()",
  );
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        const [s, p, pr] = await Promise.all([
          api.getSchema(id),
          api.listPermissions(id),
          api.listPresets(),
        ]);
        setSchema(s.data);
        const map: Record<string, Permission> = {};
        for (const x of p.data) map[x.table_name] = x;
        setPerms(map);
        setPresets(pr.data);
        const requested = searchParams.get("table") ?? "";
        const names = s.data.tables.map((t) => t.name);
        if (requested && names.includes(requested)) setTable(requested);
        else if (s.data.tables[0]) setTable(s.data.tables[0].name);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    const p = perms[table];
    if (!p) return;
    setSelect(p.select);
    setInsert(p.insert);
    setUpdate(p.update);
    setDel(p.delete);
    setOwnerColumn(p.owner_column || "user_id");
    if (p.select_using) setSelectUsing(p.select_using);
    if (p.insert_check) setInsertCheck(p.insert_check);
    if (p.update_using) setUpdateUsing(p.update_using);
    if (p.update_check) setUpdateCheck(p.update_check);
    if (p.delete_using) setDeleteUsing(p.delete_using);
    if (
      p.select === "custom" ||
      p.insert === "custom" ||
      p.update === "custom" ||
      p.delete === "custom"
    ) {
      setAdvanced(true);
    }
  }, [table, perms]);

  useEffect(() => {
    if (!id || !table) {
      setTableRLS(null);
      return;
    }
    void (async () => {
      try {
        const { data } = await api.getTableRLS(id, table);
        setTableRLS(data);
      } catch {
        setTableRLS(null);
      }
    })();
  }, [id, table]);

  const warnings: string[] = [];
  if (select === "owner" || update === "owner" || del === "owner") {
    const cols = schema?.tables.find((t) => t.name === table)?.columns ?? [];
    if (!cols.some((c) => c.name === ownerColumn)) {
      warnings.push(`Owner column "${ownerColumn}" not found on table.`);
    }
  }
  if (update === "public") warnings.push("Public UPDATE is risky.");
  if (del === "public") warnings.push("Public DELETE is risky.");
  if (
    select === "custom" ||
    insert === "custom" ||
    update === "custom" ||
    del === "custom"
  ) {
    warnings.push(
      "Custom expressions compile to PostgreSQL RLS USING / WITH CHECK. Use auth.uid() and auth.role(). No semicolons or comments.",
    );
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setMsg("");
    try {
      const body = buildPermissionBody({
        advanced,
        preset,
        select,
        insert,
        update,
        delete: del,
        ownerColumn,
        selectUsing,
        insertCheck,
        updateUsing,
        updateCheck,
        deleteUsing,
      });
      const { data } = await api.setPermission(id, table, body);
      setPerms((m) => ({ ...m, [table]: data }));
      setMsg("Permissions saved and RLS synced.");
    } catch (err) {
      setError(err);
    }
  }

  async function onToggleRLS(enabled: boolean) {
    setError(null);
    setMsg("");
    try {
      const { data } = await api.setTableRLS(id, table, { enabled });
      setTableRLS(data);
      setMsg(enabled ? "Row-level security enabled." : "Row-level security disabled.");
    } catch (err) {
      setError(err);
    }
  }

  async function onSavePolicy(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setMsg("");
    try {
      const body = buildPolicyBody({
        name: editingPolicy || policyName,
        command: policyCommand,
        roles: policyRoles,
        using: policyUsing,
        check: policyCheck,
      });
      const { data } = editingPolicy
        ? await api.alterPolicy(id, table, editingPolicy, body)
        : await api.createPolicy(id, table, body);
      setTableRLS(data);
      setEditingPolicy(null);
      setPolicyName("");
      setMsg(editingPolicy ? "Policy updated." : "Policy created.");
    } catch (err) {
      setError(err);
    }
  }

  async function onDropPolicy(name: string) {
    setError(null);
    setMsg("");
    try {
      const { data } = await api.dropPolicy(id, table, name);
      setTableRLS(data);
      if (editingPolicy === name) setEditingPolicy(null);
      setMsg("Policy dropped.");
    } catch (err) {
      setError(err);
    }
  }

  function startEdit(policy: LivePolicy) {
    setEditingPolicy(policy.name);
    setPolicyName(policy.name);
    setPolicyCommand(policy.command || "ALL");
    setPolicyRoles((policy.roles ?? []).join(", "));
    setPolicyUsing(policy.using || "");
    setPolicyCheck(policy.check || "");
  }

  function applyPolicyPreset(kind: "owner" | "authenticated" | "public" | "deny") {
    const expr = policyPresetExpression(kind, ownerColumn);
    setPolicyUsing(expr.using);
    setPolicyCheck(expr.check);
  }

  if (loading) return <Spinner label="Loading permissions…" />;

  return (
    <div>
      <PageHeader
        eyebrow="Access"
        title="Permissions"
        description="Enable row-level security, manage live PostgreSQL policies, or compile presets. SQL CREATE/ALTER/DROP POLICY in the SQL editor appears here."
      />
      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}
      {!schema || schema.tables.length === 0 ? (
        <EmptyState title="No tables to protect">
          Create a table first, then set permissions.
        </EmptyState>
      ) : (
        <>
        <div className="mb-4 max-w-2xl">
          <label className="label" htmlFor="perm-table">
            Table
          </label>
          <select
            id="perm-table"
            className="input"
            value={table}
            onChange={(e) => {
              setTable(e.target.value);
              setSearchParams(e.target.value ? { table: e.target.value } : {});
              setEditingPolicy(null);
            }}
          >
              {schema.tables.map((t) => (
                <option key={t.name} value={t.name}>
                  {t.name}
                </option>
              ))}
            </select>
        </div>

        <div className="card mb-4 max-w-2xl space-y-3 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-sans text-sm font-semibold text-text-primary">
                Row-level security
              </h3>
              <p className="mt-1 text-xs text-text-muted">
                PostgreSQL ENABLE / FORCE RLS. Default-deny when enabled with no matching policy.
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm text-text-secondary">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-border-standard text-secondary focus:ring-secondary"
                checked={Boolean(tableRLS?.enabled)}
                onChange={(e) => void onToggleRLS(e.target.checked)}
              />
              Enabled
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant={tableRLS?.enabled ? "success" : "danger"}>
              {tableRLS?.enabled ? "RLS on" : "RLS off"}
            </Badge>
            {tableRLS?.forced ? <Badge variant="secondary">FORCE</Badge> : null}
          </div>
          {tableRLS && !tableRLS.enabled && tableRLS.policies.length > 0 ? (
            <WarnBox>
              Policies exist but RLS is disabled — they are not enforced.
            </WarnBox>
          ) : null}
          {tableRLS?.enabled && tableRLS.policies.length === 0 ? (
            <WarnBox>
              RLS is on with no policy. The runtime role cannot see or change rows.
            </WarnBox>
          ) : null}
        </div>

        <form onSubmit={onSavePolicy} className="card mb-4 max-w-2xl space-y-4 p-5">
          <h3 className="font-sans text-sm font-semibold text-text-primary">
            {editingPolicy ? `Edit policy ${editingPolicy}` : "Create policy"}
          </h3>
          <p className="text-xs text-text-muted">
            Writes a real CREATE/ALTER POLICY. Use auth.uid() and auth.role(). No semicolons or comments.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="policy-name">
                Name
              </label>
              <input
                id="policy-name"
                className="input font-mono text-sm"
                value={policyName}
                disabled={Boolean(editingPolicy)}
                onChange={(e) => setPolicyName(e.target.value)}
                required={!editingPolicy}
              />
            </div>
            <div>
              <label className="label" htmlFor="policy-command">
                Command
              </label>
              <select
                id="policy-command"
                className="input"
                value={policyCommand}
                disabled={Boolean(editingPolicy)}
                onChange={(e) => setPolicyCommand(e.target.value)}
              >
                {POLICY_COMMANDS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="label" htmlFor="policy-roles">
              Roles (comma-separated, default runtime)
            </label>
            <input
              id="policy-roles"
              className="input font-mono text-sm"
              value={policyRoles}
              onChange={(e) => setPolicyRoles(e.target.value)}
              placeholder="tinybase_runtime"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {(
              [
                ["owner", "Owner"],
                ["authenticated", "Authenticated"],
                ["public", "Public"],
                ["deny", "Deny"],
              ] as const
            ).map(([kind, label]) => (
              <button
                key={kind}
                type="button"
                className="btn-secondary text-xs"
                onClick={() => applyPolicyPreset(kind)}
              >
                {label}
              </button>
            ))}
          </div>
          {policyCommandAllowsUsing(policyCommand) ? (
            <div>
              <label className="label" htmlFor="policy-using">
                USING
              </label>
              <textarea
                id="policy-using"
                className="input min-h-[4.5rem] font-mono text-xs"
                value={policyUsing}
                onChange={(e) => setPolicyUsing(e.target.value)}
                spellCheck={false}
              />
            </div>
          ) : null}
          {policyCommandAllowsCheck(policyCommand) ? (
            <div>
              <label className="label" htmlFor="policy-check">
                WITH CHECK
              </label>
              <textarea
                id="policy-check"
                className="input min-h-[4.5rem] font-mono text-xs"
                value={policyCheck}
                onChange={(e) => setPolicyCheck(e.target.value)}
                spellCheck={false}
              />
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <button className="btn-primary" type="submit">
              {editingPolicy ? "Update policy" : "Create policy"}
            </button>
            {editingPolicy ? (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setEditingPolicy(null);
                  setPolicyName("");
                }}
              >
                Cancel edit
              </button>
            ) : null}
          </div>
        </form>

        {tableRLS && tableRLS.policies.length > 0 ? (
          <div className="mb-6 table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Policy</th>
                  <th>Command</th>
                  <th>Roles</th>
                  <th>USING / CHECK</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tableRLS.policies.map((p) => (
                  <tr key={p.name}>
                    <td className="font-mono text-xs">
                      {p.name}
                      {p.managed ? (
                        <Badge variant="secondary" className="ml-2">
                          preset
                        </Badge>
                      ) : null}
                    </td>
                    <td>{p.command}</td>
                    <td className="font-mono text-xs">
                      {(p.roles ?? []).join(", ") || "—"}
                    </td>
                    <td className="max-w-[16rem] truncate font-mono text-[10px] text-text-muted">
                      {p.using || p.check || "—"}
                    </td>
                    <td className="space-x-2 whitespace-nowrap">
                      <button
                        type="button"
                        className="text-xs text-secondary hover:underline"
                        onClick={() => startEdit(p)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-xs text-error hover:underline"
                        onClick={() => void onDropPolicy(p.name)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        <form onSubmit={onSave} className="card max-w-2xl space-y-4 p-5">
          <h3 className="font-sans text-sm font-semibold text-text-primary">
            Presets
          </h3>
          <p className="text-xs text-text-muted">
            Compiles named presets into tinybase_* policies only. SQL-authored policies are kept.
          </p>

          <label className="flex items-center gap-2.5 text-sm text-text-secondary">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-border-standard text-secondary focus:ring-secondary"
              checked={advanced}
              onChange={(e) => setAdvanced(e.target.checked)}
            />
            Advanced mode (per-operation levels + custom SQL)
          </label>

          {!advanced ? (
            <div>
              <label className="label" htmlFor="preset">
                Preset
              </label>
              <select
                id="preset"
                className="input"
                value={preset}
                onChange={(e) => setPreset(e.target.value)}
              >
                {presets.map((p) => (
                  <option key={p.name} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                {(
                  [
                    ["Select", select, setSelect],
                    ["Insert", insert, setInsert],
                    ["Update", update, setUpdate],
                    ["Delete", del, setDel],
                  ] as const
                ).map(([label, val, set]) => (
                  <div key={label}>
                    <label className="label">{label}</label>
                    <select
                      className="input"
                      value={val}
                      onChange={(e) => set(e.target.value)}
                    >
                      {LEVELS.map((l) => (
                        <option key={l} value={l}>
                          {l}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              {select === "custom" ? (
                <div>
                  <label className="label" htmlFor="select-using">
                    SELECT USING
                  </label>
                  <textarea
                    id="select-using"
                    className="input min-h-[4.5rem] font-mono text-xs"
                    value={selectUsing}
                    onChange={(e) => setSelectUsing(e.target.value)}
                    spellCheck={false}
                  />
                </div>
              ) : null}
              {insert === "custom" ? (
                <div>
                  <label className="label" htmlFor="insert-check">
                    INSERT WITH CHECK
                  </label>
                  <textarea
                    id="insert-check"
                    className="input min-h-[4.5rem] font-mono text-xs"
                    value={insertCheck}
                    onChange={(e) => setInsertCheck(e.target.value)}
                    spellCheck={false}
                  />
                </div>
              ) : null}
              {update === "custom" ? (
                <>
                  <div>
                    <label className="label" htmlFor="update-using">
                      UPDATE USING
                    </label>
                    <textarea
                      id="update-using"
                      className="input min-h-[4.5rem] font-mono text-xs"
                      value={updateUsing}
                      onChange={(e) => setUpdateUsing(e.target.value)}
                      spellCheck={false}
                    />
                  </div>
                  <div>
                    <label className="label" htmlFor="update-check">
                      UPDATE WITH CHECK (optional; defaults to USING)
                    </label>
                    <textarea
                      id="update-check"
                      className="input min-h-[4.5rem] font-mono text-xs"
                      value={updateCheck}
                      onChange={(e) => setUpdateCheck(e.target.value)}
                      spellCheck={false}
                    />
                  </div>
                </>
              ) : null}
              {del === "custom" ? (
                <div>
                  <label className="label" htmlFor="delete-using">
                    DELETE USING
                  </label>
                  <textarea
                    id="delete-using"
                    className="input min-h-[4.5rem] font-mono text-xs"
                    value={deleteUsing}
                    onChange={(e) => setDeleteUsing(e.target.value)}
                    spellCheck={false}
                  />
                </div>
              ) : null}
            </>
          )}

          <div>
            <label className="label" htmlFor="owner-col">
              Owner column
            </label>
            <input
              id="owner-col"
              className="input font-mono text-sm"
              value={ownerColumn}
              onChange={(e) => setOwnerColumn(e.target.value)}
            />
          </div>

          {warnings.map((w, i) => (
            <WarnBox key={i}>{w}</WarnBox>
          ))}

          {msg ? <SuccessBox>{msg}</SuccessBox> : null}

          <button className="btn-primary" type="submit">
            Save permissions
          </button>
        </form>
        </>
      )}

      {Object.keys(perms).length > 0 ? (
        <div className="mt-6 table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Table</th>
                <th>Select</th>
                <th>Insert</th>
                <th>Update</th>
                <th>Delete</th>
                <th>Owner col</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(perms).map((p) => (
                <tr key={p.table_name}>
                  <td className="font-mono text-xs">{p.table_name}</td>
                  <td>
                    {p.select}
                    {p.select === "custom" && p.select_using ? (
                      <div className="mt-1 max-w-[12rem] truncate font-mono text-[10px] text-text-muted">
                        {p.select_using}
                      </div>
                    ) : null}
                  </td>
                  <td>{p.insert}</td>
                  <td>{p.update}</td>
                  <td>{p.delete}</td>
                  <td className="font-mono text-xs">{p.owner_column ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
